// src/App.jsx
import React, { useEffect, useState, useRef } from "react";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { TooltipProvider, Toaster } from "@kuda/ui";
import AppMenuBar from "./appBar/appBar";
import AppRoutes from "./routes/AppRoutes";
import { AppThemeProvider } from "./theme/ThemeContext";
import Loading from "./components/loading";

/**
 * This component handles showing a loading indicator while MSAL is busy
 * and only renders the main app content once authentication is resolved.
 */
const AuthenticatedContent = () => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isReady, setIsReady] = useState(false);
  const initialCheckCompleted = useRef(false);

  useEffect(() => {
    const initializeSession = async () => {
      // This effect should only run ONCE after MSAL is idle.
      if (inProgress === InteractionStatus.None && !initialCheckCompleted.current) {
        initialCheckCompleted.current = true;

        if (isAuthenticated) {
          console.log("AuthenticatedContent: User is authenticated. Attempting silent refresh...");
          const account = accounts[0];
          try {
            // Perform one initial silent refresh to get the latest roles.
            await instance.ssoSilent({
              account: account,
              scopes: ["openid", "profile", "email"],
            });
            console.log("AuthenticatedContent: Initial silent refresh successful.");
          } catch (error) {
            console.warn("AuthenticatedContent: Initial silent refresh failed. Proceeding anyway.", error);
          }
        } else {
          console.log("AuthenticatedContent: User is not authenticated. Ready to show login page.");
        }

        // The app is now ready to render, whether the user is logged in or not.
        setIsReady(true);
      }
    };

    initializeSession();
  }, [inProgress, isAuthenticated, accounts, instance]);

  // While MSAL is working OR our initial check isn't complete, show the loader.
  if (!isReady || inProgress !== InteractionStatus.None) {
    return <Loading open={true} type="skeleton" variant="table" />;
  }

  // Once ready, render the full application.
  return (
    <div>
      <AppMenuBar />
      <AppRoutes />
    </div>
  );
};

function App({ msalInstance }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AppThemeProvider>
        <TooltipProvider>
          <AuthenticatedContent />
          <Toaster />
        </TooltipProvider>
      </AppThemeProvider>
    </MsalProvider>
  );
}

export default App;